import "./RecentMission.css";
import { useState, useEffect } from "react";
import gsap from "gsap";
import FeatureWrapper from "../../../../Utils/FeatureWrapper";
import { useFeatureStatus } from "../../../../Utils/FeatureWrapper";
import { useAuth } from "../../../../Security/AuthProvider";
import PhotoViewer from "./PhotoViewer/PhotoViewer";
import "../../../../Utils/FeatureWrapper.css";
import { useNotification } from "../../../../Utils/Commons/Notification/NotificationContext";

const formatBackToHours = (timeSlot, baseHour = 8) => {
  const minutes = (timeSlot * 15) % 60;
  const hourOffset = Math.floor((timeSlot * 15) / 60);
  const hour = baseHour + hourOffset;

  return `${hour}h${minutes === 0 ? "00" : minutes}`;
};

const RecentMission = ({ mission }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [formData, setFormData] = useState(null);
  
  // États pour la visionneuse de photos
  const [showPhotoViewer, setShowPhotoViewer] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [allPhotos, setAllPhotos] = useState([]);
  
  // Hook pour vérifier les permissions PDF
  const { hasPermission: canGeneratePdf } = useFeatureStatus('pdf_generation');
  const { getCredentials } = useAuth();
  const { notifyError, notifySuccess } = useNotification();

  console.log('🚀 RecentMission: mission reçue:', mission);

  useEffect(() => {
    const cards = document.querySelectorAll(".info-card");
    cards.forEach((card) => {
      card.addEventListener("mouseenter", () => {
        gsap.to(card, {
          "--shadow-spread": "6px",
          duration: 0.15,
          ease: "power1.inOut",
        });
      });
      card.addEventListener("mouseleave", () => {
        gsap.to(card, {
          "--shadow-spread": "4px",
          duration: 0.15,
          ease: "power1.inOut",
        });
      });
    });

    return () => {
      cards.forEach((card) => {
        card.removeEventListener("mouseenter", () => {});
        card.removeEventListener("mouseleave", () => {});
      });
    };
  }, []);

  useEffect(() => {
    if (mission.form_data) {
      try {
        console.log('📝 Parsing form_data:', mission.form_data);
        const parsed = JSON.parse(mission.form_data);
        console.log('✅ Form data parsed:', parsed);
        setFormData(parsed);
      } catch (error) {
        console.error('❌ Erreur parsing form_data:', error);
      }
    }
  }, [mission.form_data]);

  // Function to generate PDF
  const handleGeneratePdf = async () => {
    try {
      setGeneratingPdf(true);
      
      const credentials = getCredentials();
      
      console.log('🎯 FRONTEND: Génération PDF pour mission', mission.id, 'entreprise', credentials?.company_name);
      
      // 🚀 Préparer toutes les données déjà traitées par le frontend
      const formattedFormData = formatFormData(mission.form_data);
      
      // Structurer les données du client
      const clientInfo = {
        name: mission.client_name || "Client non spécifié",
        address: mission.adresse || "Adresse non spécifiée",
        phone: "", // Pas dans les données actuelles
        email: "", // Pas dans les données actuelles
        date: formattedDate,
        time: mission.beginning ? formatBackToHours(mission.beginning) : "Heure non spécifiée",
        operationName: mission.operation_name || "Opération"
      };
      
      // Structurer les données de formulaire (texte)
      const formData = {};
      formData["Agent assigné"] = mission.agent_name || "Non spécifié";
      formData["Statut"] = mission.status || "En cours";
      
      // Ajouter les données du formulaire formatées
      formattedFormData.forEach(field => {
        if (field.type !== 'image') {
          // Pour les champs non-image, utiliser la valeur brute
          formData[field.label] = field.value;
        }
      });
      
      // Structurer les photos du formulaire
      const formPhotos = [];
      formattedFormData.forEach(field => {
        if (field.type === 'image' && field.value) {
          let photoSrc = field.value;
          
          // S'assurer que l'image a le bon format
          if (!photoSrc.startsWith('data:image/')) {
            photoSrc = `data:image/jpeg;base64,${photoSrc}`;
          }
          
          formPhotos.push({
            src: photoSrc,
            label: field.label
          });
        }
      });
      
      // Structurer les photos de mission
      const missionPhotos = photos.map(photo => {
        if (photo.startsWith('data:image/')) {
          return photo;
        }
        return `data:image/jpeg;base64,${photo}`;
      });
      
      // Structurer la signature
      let clientSignature = null;
      if (signature) {
        clientSignature = signature.startsWith('data:image/') ? signature : `data:image/png;base64,${signature}`;
      }
      
      console.log('📤 FRONTEND: Envoi des données structurées:', {
        clientInfo,
        formDataKeys: Object.keys(formData),
        formPhotosCount: formPhotos.length,
        missionPhotosCount: missionPhotos.length,
        hasSignature: !!clientSignature
      });
      
      const response = await fetch(`/pdf/generate-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          rdv_id: mission.id,
          company_name: credentials?.company_name,
          company_password: credentials?.company_password || '',
          // 🚀 Envoyer directement les données déjà traitées !
          clientInfo,
          formData,
          formPhotos,
          missionPhotos,
          signature: clientSignature
        })
      });

      if (response.ok) {
        // Télécharger le PDF
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `mission_${mission.client_name}_${mission.date}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        notifySuccess('PDF généré avec succès !');
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.message || errorData.error || 'Erreur inconnue';
        notifyError('Erreur lors de la génération du PDF: ' + errorMessage);
      }
    } catch (error) {
      notifyError('Erreur lors de la génération du PDF: ' + error.message);
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Format date to display nicely
  const formattedDate =
    mission.date instanceof Date
      ? mission.date.toLocaleDateString("fr-FR", {
          year: "numeric",
          month: "short",
          day: "2-digit",
        })
      : mission.date;

  // Parse photos and signature from JSON if they exist
  const photos = mission.photos ? JSON.parse(mission.photos) : [];
  const signature = mission.client_signature;

  // Fonction pour détecter les images base64 par leurs signatures
  const isValidBase64Image = (str) => {
    console.log('🔍 isValidBase64Image - input:', typeof str, str?.substring(0, 100));
    
    if (typeof str !== 'string' || str.length < 50) {
      console.log('❌ isValidBase64Image - trop court ou pas string');
      return false;
    }

    // Supprimer les éventuels préfixes data: et espaces
    const cleanStr = str.replace(/^data:image\/[^;]+;base64,/, '').trim();
    console.log('🧹 isValidBase64Image - cleanStr:', cleanStr.substring(0, 50));

    // Vérifier les signatures d'en-tête des formats d'image courants
    const imageSignatures = {
      JPEG: '/9j/',      // JPEG
      PNG: 'iVBORw0KGgo', // PNG
      GIF: 'R0lGOD',     // GIF
      WebP: 'UklGR'      // WebP
    };

    for (const [format, signature] of Object.entries(imageSignatures)) {
      if (cleanStr.startsWith(signature)) {
        console.log(`✅ isValidBase64Image - ${format} détecté`);
        return true;
      }
    }

    console.log('❌ isValidBase64Image - aucune signature reconnue');
    return false;
  };

  // Fonction pour déduire le type de champ
  const deduceFieldType = (value) => {
    console.log('🔍 deduceFieldType - input:', typeof value, value?.substring(0, 100));
    
    if (typeof value === 'string') {
      // Vérifier si c'est une image (avec ou sans préfixe data:)
      if (value.startsWith('data:image/') || isValidBase64Image(value)) {
        console.log('✅ deduceFieldType - IMAGE détectée');
        return 'image';
      }
      
      // Pour les autres types de texte
      if (value.includes('\n') || value.length > 100) {
        console.log('✅ deduceFieldType - TEXTAREA détecté');
        return 'textarea';
      }
    }
    
    console.log('✅ deduceFieldType - TEXT par défaut');
    return 'text';
  };

  // Fonction pour obtenir l'icône appropriée selon le type
  const getFieldIcon = (fieldType) => {
    const icons = {
      'text': '📝',
      'textarea': '📄',
      'checkbox': '☑️',
      'radio': '🔘',
      'select': '📋',
      'image': '🖼️',
      'file': '📎',
      'date': '📅',
      'time': '⏰',
      'email': '📧',
      'phone': '📞',
      'number': '🔢'
    };
    return icons[fieldType] || '📝';
  };

  // Fonction pour formater la valeur d'un champ selon son type
  const formatFieldValue = (value, fieldType = 'text') => {
    console.log(`🎨 formatFieldValue - Type: ${fieldType}, Value:`, typeof value === 'string' ? value.substring(0, 100) : value);
    
    if (value === null || value === undefined || value === '') {
      console.log('❌ formatFieldValue - valeur vide');
      return 'Non renseigné';
    }
    
    // Gestion spécifique selon le type de champ
    switch (fieldType) {
      case 'checkbox':
        console.log('☑️ formatFieldValue - Traitement CHECKBOX');
        if (Array.isArray(value)) {
          return value.length > 0 ? value.join(', ') : 'Aucune sélection';
        }
        return typeof value === 'boolean' ? (value ? 'Oui' : 'Non') : String(value);
      
      case 'radio':
        console.log('🔘 formatFieldValue - Traitement RADIO');
        return String(value);
      
      case 'image':
        console.log('🖼️ formatFieldValue - Traitement IMAGE');
        console.log('🖼️ DEBUG - Value type:', typeof value);
        console.log('🖼️ DEBUG - Value length:', value?.length);
        console.log('🖼️ DEBUG - Value starts with data:', value?.startsWith('data:'));
        
        if (typeof value === 'string') {
          let imageSrc = value;
          
          // Si l'image a déjà le préfixe data:image/
          if (value.startsWith('data:image/')) {
            console.log('✅ Image avec préfixe data: détectée');
          }
          // Si c'est du base64 sans préfixe, l'ajouter
          else if (isValidBase64Image(value)) {
            console.log('➕ Ajout du préfixe data:image pour base64 pur');
            imageSrc = `data:image/jpeg;base64,${value}`;
          }
          
          console.log('🖼️ DEBUG - Final imageSrc:', imageSrc.substring(0, 100));
          console.log('🖼️ DEBUG - Créer élément img avec src:', imageSrc.substring(0, 50) + '...');
          
          return (
            <div style={{ margin: '10px 0' }}>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img 
                  src={imageSrc} 
                  alt="Photo" 
                  style={{
                    maxWidth: '300px', 
                    maxHeight: '300px', 
                    borderRadius: '8px', 
                    border: '2px solid #007bff', 
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    display: 'block',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease'
                  }}
                  onLoad={(e) => {
                    console.log('✅ SUCCESS - Image chargée:', e.target.src.substring(0, 100));
                    console.log('✅ SUCCESS - Image dimensions:', e.target.naturalWidth, 'x', e.target.naturalHeight);
                  }}
                  onError={(e) => {
                    console.error('❌ ERROR - Échec chargement image:', e.target.src.substring(0, 100));
                    console.error('❌ ERROR - Image error event:', e);
                  }}
                  onLoadStart={() => console.log('🔄 Image - Début du chargement')}
                  onLoadEnd={() => console.log('🏁 Image - Fin du chargement')}
                  onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
                  onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
                  onClick={() => {
                    // Ouvrir la visionneuse de photos avec cette image
                    const photoData = [{
                      src: imageSrc,
                      alt: 'Photo du formulaire',
                      info: `Taille: ${Math.round(value.length / 1024)}KB`
                    }];
                    setAllPhotos(photoData);
                    setCurrentPhotoIndex(0);
                    setShowPhotoViewer(true);
                  }}
                />
              </div>
              <p style={{ fontSize: '12px', color: '#666', margin: '8px 0 0 0', textAlign: 'center' }}>
                📸 {Math.round(value.length / 1024)}KB - Cliquer pour agrandir
              </p>
            </div>
          );
        }
        console.log('❌ formatFieldValue - IMAGE invalide');
        return 'Aucune image';
      
      case 'text':
      case 'textarea':
        console.log('📝 formatFieldValue - Traitement TEXT/TEXTAREA');
        const stringValue = String(value);
        if (stringValue.length > 100) {
          return stringValue.substring(0, 100) + '...';
        }
        return stringValue;
      
      default:
        console.log('📄 formatFieldValue - Traitement par défaut');
        if (Array.isArray(value)) {
          return value.join(', ');
        }
        if (typeof value === 'boolean') {
          return value ? 'Oui' : 'Non';
        }
        if (typeof value === 'object') {
          return JSON.stringify(value, null, 2);
        }
        return String(value);
    }
  };

  // Fonction pour formater et organiser les données du formulaire
  const formatFormData = (formDataString) => {
    try {
      console.log("🔍 formatFormData - Début formatage:", formDataString?.substring(0, 200));
      console.log("🔍 formatFormData - Labels disponibles:", mission.form_field_labels);
      
      if (!formDataString) {
        console.log("❌ formatFormData - Pas de données");
        return [];
      }

      let formData;
      try {
        formData = JSON.parse(formDataString);
        console.log("✅ formatFormData - JSON parsé:", formData);
      } catch (parseError) {
        console.warn("⚠️ formatFormData - Erreur parsing JSON:", parseError);
        return [];
      }
      
      // Si formData est un objet avec des clés numériques (IDs de champs)
      if (typeof formData === 'object' && formData !== null) {
        const formattedData = [];
        
        Object.entries(formData).forEach(([fieldId, value]) => {
          // Utiliser les labels du backend si disponibles
          const fieldInfo = mission.form_field_labels && mission.form_field_labels[fieldId];
          
          console.log(`🔍 formatFormData - Champ ${fieldId}:`, {
            value: typeof value === 'string' && value.length > 50 ? value.substring(0, 50) + '...' : value,
            fieldInfo: fieldInfo,
            detectedType: deduceFieldType(value)
          });
          
          // Créer un label plus lisible si pas de label spécifique
          let displayLabel = `Champ ${fieldId}`;
          if (fieldInfo && fieldInfo.label) {
            displayLabel = fieldInfo.label;
          } else {
            // Essayer de déduire le type de champ à partir de la valeur
            if (typeof value === 'boolean') {
              displayLabel = `Question ${fieldId}`;
            } else if (Array.isArray(value)) {
              displayLabel = `Sélection ${fieldId}`;
            } else if (typeof value === 'string' && value.length > 50) {
              displayLabel = `Commentaire ${fieldId}`;
            }
          }

          // Utiliser le type du backend en priorité, sinon déduire
          const fieldType = (fieldInfo && fieldInfo.type) ? fieldInfo.type : deduceFieldType(value);
          
          console.log(`🏷️ formatFormData - Type final pour ${fieldId}: ${fieldType}`);
          
          const fieldData = {
            id: fieldId,
            label: displayLabel,
            type: fieldType,
            icon: getFieldIcon(fieldType),
            value: value,
            displayValue: formatFieldValue(value, fieldType)
          };
          
          console.log(`✅ formatFormData - Champ formaté ${fieldId}:`, {
            label: fieldData.label,
            type: fieldData.type,
            hasImage: typeof value === 'string' && (value.startsWith('data:image/') || isValidBase64Image(value))
          });
          
          formattedData.push(fieldData);
        });
        
        // Trier par ID de champ pour un affichage cohérent
        formattedData.sort((a, b) => {
          const aNum = parseInt(a.id);
          const bNum = parseInt(b.id);
          if (isNaN(aNum) || isNaN(bNum)) {
            return a.id.localeCompare(b.id);
          }
          return aNum - bNum;
        });
        
        console.log(`✅ formatFormData - ${formattedData.length} champs formatés:`, formattedData);
        return formattedData;
      }
      
      return [];
    } catch (error) {
      console.error('❌ formatFormData - Erreur:', error);
      return [];
    }
  };

  useEffect(() => {
    console.log('🎯 RecentMission - Mission complète:', mission);
  }, [mission]);

  console.log('🎯 RecentMission - Rendu final');

  return (
    <>
      <div className="recent-mission info-card">
        <p className="info-card-name">{mission.client_name}</p>
        <div className="info-card-name-underline"></div>
        <p className="info-card-label">Date</p>
        <p className="info-card-value">{formattedDate}</p>
        <p className="info-card-label">Opération</p>
        <p className="info-card-value">
          {mission.operation_name || "Non spécifiée"}
        </p>
        <p className="info-card-label">Adresse</p>
        <p className="info-card-value">{mission.adresse || "Non spécifiée"}</p>
        <div className="info-card-buttons row">
          <button onClick={() => setShowDetails(true)}>Voir les détails</button>
          
          {/* Bouton PDF visible uniquement pour Business/Premium */}
          <FeatureWrapper feature="pdf_generation">
            <button 
              onClick={() => {
                console.log('🎯 FRONTEND: Clic bouton PDF principal');
                handleGeneratePdf();
              }}
              disabled={generatingPdf}
              className="pdf-button"
            >
              {generatingPdf ? '📄 Génération...' : '📄 Générer PDF'}
            </button>
          </FeatureWrapper>
          
          {/* Message d'upgrade si pas de permission PDF */}
          {!canGeneratePdf && (
            <div className="feature-locked-hint">
              <span className="locked-icon">🔒</span>
              <span className="locked-text">PDF Business+</span>
            </div>
          )}
        </div>
      </div>

      {showDetails && (
        <div className="popup-container" onClick={() => setShowDetails(false)}>
          <div
            className="mission-details-popup"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="popup-header">
            <h2>Détails de la mission</h2>
              
              {/* Bouton PDF dans le popup aussi */}
              <div className="popup-actions">
                <FeatureWrapper feature="pdf_generation">
                  <button 
                    onClick={() => {
                      console.log('🎯 FRONTEND: Clic bouton PDF dans popup');
                      handleGeneratePdf();
                    }}
                    disabled={generatingPdf}
                    className="pdf-button-popup"
                  >
                    {generatingPdf ? '📄 Génération...' : '📄 Télécharger PDF'}
                  </button>
                </FeatureWrapper>
              </div>
            </div>
            
            <div className="mission-details-content">
              <div className="detail-group">
                <label>Client:</label>
                <p>{mission.client_name}</p>
              </div>
              <div className="detail-group">
                <label>Agent:</label>
                <p>{mission.agent_name || "Non spécifié"}</p>
              </div>
              <div className="detail-group">
                <label>Date:</label>
                <p>{formattedDate}</p>
              </div>
              <div className="detail-group">
                <label>Horaires:</label>
                <p>
                  {formatBackToHours(mission.beginning)} -{" "}
                  {formatBackToHours(mission.ending)}
                </p>
              </div>
              <div className="detail-group">
                <label>Adresse:</label>
                <p>{mission.adresse || "Non spécifiée"}</p>
              </div>
              <div className="detail-group">
                <label>Opération:</label>
                <p>{mission.operation_name || "Non spécifiée"}</p>
              </div>

              {/* Photos Section */}
              {photos.length > 0 && (
                <div className="detail-group photos-group">
                  <label>
                    Photos de la mission ({photos.length} photo{photos.length > 1 ? 's' : ''}):
                    {photos.length > 1 && (
                      <button
                        style={{
                          marginLeft: '10px',
                          background: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '6px 12px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => {
                          const photoData = photos.map((photo, index) => ({
                            src: `data:image/jpeg;base64,${photo}`,
                            alt: `Photo ${index + 1}`,
                            info: `Photo ${index + 1} de ${photos.length}`
                          }));
                          setAllPhotos(photoData);
                          setCurrentPhotoIndex(0);
                          setShowPhotoViewer(true);
                        }}
                        onMouseOver={(e) => e.target.style.background = '#0056b3'}
                        onMouseOut={(e) => e.target.style.background = '#007bff'}
                      >
                        📸 Voir toutes les photos
                      </button>
                    )}
                  </label>
                  <div className="photos-grid">
                    {photos.map((photo, index) => (
                      <div key={index} className="photo-container">
                        <img
                          src={`data:image/jpeg;base64,${photo}`}
                          alt={`Photo ${index + 1}`}
                          className="mission-photo"
                          onClick={() => {
                            const photoData = photos.map((photo, idx) => ({
                              src: `data:image/jpeg;base64,${photo}`,
                              alt: `Photo ${idx + 1}`,
                              info: `Photo ${idx + 1} de ${photos.length}`
                            }));
                            setAllPhotos(photoData);
                            setCurrentPhotoIndex(index);
                            setShowPhotoViewer(true);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Signature Section */}
              {signature && (
                <div className="detail-group signature-group">
                  <label>Signature du client:</label>
                  <img
                    src={`data:image/png;base64,${signature}`}
                    alt="Signature du client"
                    className="client-signature"
                  />
                </div>
              )}

              {/* Section des données du formulaire - DIRECTEMENT VISIBLE */}
              {mission.form_data && (
                <div className="detail-group form-data-group">
                  <label>Informations du formulaire:</label>
                  <div className="form-data-content">
                    {formatFormData(mission.form_data).length > 0 ? (
                      <div className="form-fields-grid">
                        {formatFormData(mission.form_data).map((field, index) => {
                          console.log(`🎨 RENDER - Affichage du champ ${field.id}:`, field);
                          return (
                            <div key={field.id || index} className="form-field-item">
                              <div className="form-field-label">
                                <span className="field-icon">{field.icon}</span>
                                <span className="field-text">{field.label}</span>
                              </div>
                              <div className="form-field-value">
                                {field.displayValue}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="form-data-fallback">
                        <p className="fallback-label">Données brutes :</p>
                        <pre className="fallback-data">
                          {JSON.stringify(JSON.parse(mission.form_data), null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <button
              className="close-button"
              onClick={() => setShowDetails(false)}
            >
              Fermer
            </button>
          </div>
        </div>
      )}

      {showPhotoViewer && (
        <PhotoViewer
          isOpen={showPhotoViewer}
          photos={allPhotos}
          initialIndex={currentPhotoIndex}
          onClose={() => setShowPhotoViewer(false)}
        />
      )}
    </>
  );
};

export default RecentMission;
